import { useState, useEffect } from "react";

// ============================================================
// STRIPE CONFIGURATION
// ============================================================
// YOUR main payment link:
const STRIPE_MAIN_URL = "https://buy.stripe.com/YOUR_LINK_HERE";
//
// PARTNER LINKS — add partners here. They share your normal
// website URL with ?ref=CODE on the end, e.g.:
//   tradecvbuilder.uk?ref=vb
//
// The page automatically uses their Stripe link instead of yours.
// You track their sales separately in your Stripe dashboard.
const PARTNER_LINKS = {
  vb: "https://buy.stripe.com/VENESKY_BROWN_LINK_HERE",
  // Add more partners: "code": "https://buy.stripe.com/THEIR_LINK",
};
// ============================================================

function getStripeUrl() {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  if (ref && PARTNER_LINKS[ref]) return PARTNER_LINKS[ref];
  return STRIPE_MAIN_URL;
}

const SINGLE_BUNDLE = {
  name: "Construction CV Complete Bundle",
  price: "£9.99",
  period: "one-off payment · instant access",
  features: [
    "Trade CV Builder (7-step guided form for experienced workers)",
    "New to the Industry CV Builder (for school leavers & beginners)",
    "UK English auto spell & grammar checker",
    "PDF, Word & HTML instant downloads (3 included)",
    "3 CV templates (Clean & Minimal, Professional, Bold & Modern)",
    "43 construction skills selector",
    "23 work experience examples covering all construction roles",
    "Full references system (manual entry + email request tool)",
    "Interactive 25-step job checklist (CV to first day on site)",
    "Downloadable trade-by-trade CV helpsheet",
    "Full CSCS card guide (all 6 card types)",
    "PPE & safety equipment guide",
    "Site preparation & first day checklist",
    "Construction site roles & current 2026 UK pay rates",
    "Interview questions with model answers",
    "Drug & alcohol testing guide",
    "New to Industry Tips guide for school leavers & apprentices",
    "12 apprenticeship roles with wages & qualifications",
    "Transferable skills guide (retail, warehouse, landscaping)",
    "Free lifetime updates",
  ],
  guarantees: [
    "Instant access after payment",
    "No subscriptions — pay once, keep forever",
    "Secure checkout via Stripe",
    "3 CV downloads included — extra credits available",
  ],
};

const TESTIMONIALS = [
  { name: "The First-Timer", location: "", role: "No CV experience", text: "Never written a CV before? The step-by-step builder walks you through every section with tips and examples. Just fill in the blanks and download.", stars: 0 },
  { name: "The Experienced Tradesman", location: "", role: "Groundworker / Bricklayer", text: "Already know your trade but struggle to put it on paper? Pick from 23 ready-made work experience examples, tweak the details, and you are done.", stars: 0 },
  { name: "The Career Changer", location: "", role: "Coming from retail or warehouse", text: "Switching from another industry? The transferable skills guide shows you exactly how to make retail, warehouse, or kitchen experience relevant to construction employers.", stars: 0 },
  { name: "The Apprentice", location: "", role: "School leaver / College student", text: "Just finished school or college? The New to the Industry CV Builder has pre-written personal statements, school subject guidance, and a checklist to get you on site.", stars: 0 },
  { name: "The Non-Native Speaker", location: "", role: "English as second language", text: "Worried about spelling and grammar? The built-in UK English checker catches mistakes and corrects US spellings automatically as you type.", stars: 0 },
  { name: "The Supervisor", location: "", role: "Foreman / Site Supervisor", text: "Moving up to a supervisory role? Work experience examples cover everything from labourer to site manager, with the right terminology for each level.", stars: 0 },
];

const FAQ = [
  { q: "Do I need any experience to use this?", a: "Not at all. The CV builder is designed specifically for people with little or no experience writing CVs. We even have a dedicated 'New to Industry Tips' section for school leavers and young people with ready-made personal statements, transferable skills examples from retail and warehouse work, and a step-by-step guide on how to get into construction." },
  { q: "What exactly do I get for £9.99?", a: "Everything — two CV builders (Trade CV Builder for experienced workers and New to the Industry CV Builder for school leavers), 3 professional templates, UK English checker, PDF and Word downloads, 23 work experience examples, a smart references system with email request tool, a printable trade-by-trade helpsheet, an interactive 25-step job checklist, plus complete guides on CSCS cards, PPE, site preparation, interview questions, drug testing, site roles with current 2026 UK pay rates, and a full apprenticeship guide. One-off payment with free lifetime updates." },
  { q: "Can I download my CV as a PDF?", a: "Yes — PDF, Word, and HTML downloads are all included. Your £9.99 bundle includes 3 CV downloads. You can build, edit, and preview your CV as many times as you like — the limit only applies to final downloads. If you need more, extra download packs are available from £2.99." },
  { q: "Why are downloads limited?", a: "Your £9.99 purchase is a personal licence for one person. The 3-download limit means you can download your CV in different formats or update it as needed. If you need more downloads — for example, if you are updating your CV regularly — you can purchase extra download credits: 3 for £2.99, 10 for £4.99, or unlimited for £7.99." },
  { q: "I'm a school leaver — will this work for me?", a: "Absolutely. The 'New to Industry Tips' section was built specifically for school leavers and young people. It includes 4 personal statement examples for different situations, guidance on which school subjects to include, examples of how to write up retail, warehouse, and landscaping experience for a construction CV, a list of 12 apprenticeship trades with wages, and an interactive getting-started checklist you can tick off as you go — from getting your CSCS card to landing your first job." },
  { q: "Is this only for labourers?", a: "No — we cover 23 different roles from entry level to management. The work experience examples include ready-made templates for general labourers, groundworkers, hod carriers, demolition operatives, scaffolders, painters, bricklayers, carpenters, plasterers, plumbers, roofers, dry liners, steel fixers, plant operatives, road workers, electrician's mates, tilers, concreting operatives, drainage operatives, insulation installers, window fitters, fencing operatives, and warehouse operatives. The skills selector includes 43 construction-specific skills across trades, certifications, and core skills." },
  { q: "How do the references work?", a: "You have three options — 'Available upon request' (the standard approach), add your referees' details manually so they appear on your CV, or use our built-in email tool which opens a pre-written professional reference request email in your email app. You just click send and your referee receives a polished request asking them to provide a reference for you." },
  { q: "Will this help me get my CSCS card?", a: "We provide a comprehensive guide explaining every CSCS card type, what qualifications you need, and exactly how to apply. We don't provide the actual training or test, but we'll point you in the right direction so you know exactly what to do." },
];

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileNav(false);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", color: "#1a1a1a", background: "#FAF7F4", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=Outfit:wght@700;800;900&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-5%, -10%); }
          30% { transform: translate(3%, -15%); }
          50% { transform: translate(12%, 9%); }
          70% { transform: translate(9%, 4%); }
          90% { transform: translate(-1%, 7%); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .hero-section {
          position: relative;
          min-height: 92vh;
          display: flex;
          align-items: center;
          background: linear-gradient(165deg, #1a1a1a 0%, #2d1f14 35%, #4a2810 65%, #BF360C 100%);
          overflow: hidden;
        }
        .hero-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            90deg,
            transparent,
            transparent 98px,
            rgba(255,255,255,0.03) 98px,
            rgba(255,255,255,0.03) 100px
          );
          pointer-events: none;
        }
        .hero-section::after {
          content: '';
          position: absolute;
          inset: -50%;
          background: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E");
          opacity: 0.12;
          pointer-events: none;
          animation: grain 8s steps(10) infinite;
        }

        .diagonal-stripe {
          position: absolute;
          top: -10%;
          right: -5%;
          width: 45%;
          height: 120%;
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 18px,
            rgba(230,81,0,0.08) 18px,
            rgba(230,81,0,0.08) 20px
          );
          pointer-events: none;
        }

        .cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 18px 40px;
          background: #E65100;
          color: white;
          font-size: 17px;
          font-weight: 800;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          letter-spacing: 0.5px;
          position: relative;
          overflow: hidden;
        }
        .cta-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          background-size: 200% 100%;
          animation: shimmer 3s infinite;
        }
        .cta-btn:hover {
          background: #BF360C;
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(230,81,0,0.4);
        }

        .section-heading {
          font-family: 'Outfit', sans-serif;
          font-weight: 900;
          font-size: clamp(28px, 5vw, 44px);
          color: #1a1a1a;
          line-height: 1.1;
          margin-bottom: 12px;
        }

        .card-hover {
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }

        .plan-card {
          border-radius: 16px;
          padding: 32px 28px;
          background: white;
          border: 2px solid #e8e0d8;
          position: relative;
          overflow: hidden;
          transition: all 0.35s ease;
        }

        .faq-item {
          border-bottom: 1px solid #e8e0d8;
          cursor: pointer;
          transition: background 0.2s;
        }
        .faq-item:hover {
          background: #FFF8F3;
        }

        .testimonial-card {
          background: white;
          border-radius: 14px;
          padding: 24px;
          border: 1px solid #ede6df;
          transition: all 0.3s ease;
          min-width: 300px;
        }
        .testimonial-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 36px rgba(0,0,0,0.08);
        }

        .nav-link {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s;
          background: none;
          border: none;
          padding: 8px 14px;
        }
        .nav-link:hover { color: #FFB74D; }

        .feature-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
        }

        .stat-item {
          text-align: center;
          padding: 24px 16px;
        }

        @media (max-width: 768px) {
          .hero-content { padding: 80px 20px 40px !important; }
          .nav-links-desktop { display: none !important; }
          .mobile-nav-btn { display: flex !important; }
          .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .testimonials-scroll { flex-direction: column; align-items: stretch; }
          .testimonial-card { min-width: auto; }
        }
      `}</style>

      {/* Navigation */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? "rgba(26,26,26,0.97)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        transition: "all 0.4s ease",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center", height: 64 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>🏗️</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 18, color: "white", letterSpacing: -0.5 }}>Trade CV Builder</span>
          </div>
          <div className="nav-links-desktop" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button className="nav-link" onClick={() => scrollTo("features")}>Features</button>
            <button className="nav-link" onClick={() => scrollTo("guides")}>Guides</button>
            <button className="nav-link" onClick={() => scrollTo("pricing")}>Pricing</button>
            <button className="nav-link" onClick={() => scrollTo("reviews")}>Reviews</button>
            <button className="nav-link" onClick={() => scrollTo("faq")}>FAQ</button>
            <button onClick={() => scrollTo("pricing")} style={{ marginLeft: 12, padding: "9px 22px", background: "#E65100", color: "white", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "all 0.2s" }}>Get Started</button>
          </div>
          <button className="mobile-nav-btn" onClick={() => setMobileNav(!mobileNav)} style={{ display: "none", alignItems: "center", justifyContent: "center", width: 40, height: 40, background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, cursor: "pointer", color: "white", fontSize: 20 }}>
            {mobileNav ? "✕" : "☰"}
          </button>
        </div>
        {mobileNav && (
          <div style={{ background: "rgba(26,26,26,0.98)", padding: "12px 24px 20px", display: "flex", flexDirection: "column", gap: 4, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            {["features","guides","pricing","reviews","faq"].map(id => (
              <button key={id} className="nav-link" onClick={() => scrollTo(id)} style={{ textAlign: "left", padding: "12px 0", fontSize: 16, textTransform: "capitalize" }}>{id}</button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="hero-section">
        <div className="diagonal-stripe" />
        <div className="hero-content" style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto", padding: "120px 40px 60px", width: "100%" }}>
          <div style={{ maxWidth: 680, animation: "fadeUp 0.8s ease forwards" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(230,81,0,0.2)", border: "1px solid rgba(230,81,0,0.4)", borderRadius: 100, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, background: "#4CAF50", borderRadius: "50%", animation: "pulse 2s infinite" }} />
              <span style={{ color: "#FFB74D", fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>Built for UK Construction Workers</span>
            </div>

            <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: "clamp(36px, 6.5vw, 64px)", color: "white", lineHeight: 1.05, marginBottom: 20, letterSpacing: -1.5 }}>
              Build a CV That<br />
              <span style={{ color: "#FFB74D" }}>Gets You on Site</span>
            </h1>

            <p style={{ fontSize: "clamp(16px, 2vw, 20px)", color: "rgba(255,255,255,0.75)", lineHeight: 1.6, marginBottom: 32, maxWidth: 540 }}>
              The only CV builder made for construction. Step-by-step guidance, ready-made examples for every trade, 3 professional templates, UK English checks, and everything you need to know about CSCS cards, PPE, interviews, and working on building sites. Perfect for experienced workers, school leavers, and apprentices.
            </p>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
              <button className="cta-btn" onClick={() => scrollTo("pricing")}>
                Build Your CV Now <span style={{ fontSize: 20 }}>→</span>
              </button>
              <button onClick={() => scrollTo("features")} style={{ padding: "18px 32px", background: "rgba(255,255,255,0.08)", color: "white", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer", transition: "all 0.2s", backdropFilter: "blur(4px)" }}>
                See What's Included
              </button>
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {["No experience needed", "Instant download", "One-off payment"].map((t, i) => (
                <span key={i} style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#4CAF50", fontSize: 16 }}>✓</span> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Floating CV preview mockup */}
          <div style={{ position: "absolute", right: "5%", top: "50%", transform: "translateY(-50%)", width: 320, display: "none", animation: "float 6s ease-in-out infinite" }} className="hero-mockup">
            <div style={{ background: "white", borderRadius: 12, padding: 24, boxShadow: "0 40px 100px rgba(0,0,0,0.4)", transform: "rotate(2deg)" }}>
              <div style={{ height: 12, width: "60%", background: "#1a1a1a", borderRadius: 4, marginBottom: 8 }} />
              <div style={{ height: 8, width: "80%", background: "#e0e0e0", borderRadius: 4, marginBottom: 16 }} />
              <div style={{ height: 3, background: "#E65100", borderRadius: 4, marginBottom: 16 }} />
              {[1,2,3].map(n => <div key={n} style={{ height: 6, width: `${90-n*10}%`, background: "#f0f0f0", borderRadius: 3, marginBottom: 6 }} />)}
            </div>
          </div>
        </div>
      </section>

      {/* Built for Workers Like You */}
      <section id="reviews" style={{ background: "#FFF8F3", padding: "60px 24px", borderTop: "3px solid #E65100", borderBottom: "1px solid #ede6df" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <span style={{ color: "#E65100", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>Who Is This For?</span>
            <h2 className="section-heading" style={{ marginTop: 8 }}>Built for Workers Like You</h2>
          </div>
          <div className="testimonials-scroll" style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none" }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="testimonial-card">
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: `hsl(${i * 55 + 15}, 45%, 88%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: `hsl(${i * 55 + 15}, 45%, 40%)` }}>
                    {t.name.split(" ").pop()[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: "#E65100", fontWeight: 600 }}>{t.role}</div>
                  </div>
                </div>
                <p style={{ fontSize: 14, color: "#444", lineHeight: 1.65 }}>{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span style={{ color: "#E65100", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>Everything You Need</span>
          <h2 className="section-heading" style={{ marginTop: 8 }}>More Than Just a CV Builder</h2>
          <p style={{ color: "#777", fontSize: 17, maxWidth: 580, margin: "12px auto 0" }}>We don't just help you write a CV — we prepare you for every step of getting work on a construction site.</p>
        </div>

        <div className="feature-grid">
          {[
            { icon: "📝", title: "Trade CV Builder", desc: "7-step guided form for experienced workers. Walks you through every section with construction-specific examples, UK English checker, and 43 skills to select from." },
            { icon: "🎓", title: "New to the Industry CV Builder", desc: "Simplified CV builder for school leavers, college students, and career changers. Pre-written personal statements, beginner-friendly skills, and step-by-step guidance." },
            { icon: "📋", title: "23 Work Experience Examples", desc: "Ready-made templates covering all major construction roles — labourer, groundworker, bricklayer, carpenter, plasterer, plumber, roofer, tiler, dry liner, steel fixer, plant operative, and more." },
            { icon: "🇬🇧", title: "UK English Auto-Checker", desc: "Catches US spellings, grammar mistakes, and common typos as you type. Your CV will be polished and professional, written in proper British English." },
            { icon: "🎨", title: "3 CV Templates", desc: "Choose from Clean & Minimal, Professional, or Bold & Modern templates. Switch between them instantly and download whichever suits your style." },
            { icon: "📨", title: "Smart References System", desc: "Add references manually, or use the built-in email tool to send professional reference requests to your previous employers. Pre-written email opens in your email app." },
            { icon: "✅", title: "Interactive Job Checklist", desc: "25-step tick-off checklist covering CV building, getting site-ready, finding work, and landing the job. Track your progress from start to first day on site." },
            { icon: "🪪", title: "CSCS Card Guide", desc: "Full breakdown of every CSCS card — Green, Blue, Gold, Black, Red, and White. What they mean, what you need, and exactly how to apply." },
            { icon: "🦺", title: "PPE & Safety Guide", desc: "Every piece of PPE explained — what standard it needs to meet, when to wear it, and where to buy it. Hard hats, boots, hi-vis, gloves, RPE, and more." },
            { icon: "👷", title: "Site Roles & Pay Rates", desc: "From labourer to site manager — every role explained with current 2026 UK pay rates, daily duties, and the qualifications you need to progress." },
            { icon: "💼", title: "Interview Preparation", desc: "7 common construction interview questions with model answers. Plus what to wear, what to bring, and how to follow up afterwards." },
            { icon: "🧪", title: "Drug Testing Guide", desc: "How testing works, what substances are screened, when tests happen, and what happens if you pass or fail. No surprises on your first day." },
            { icon: "📖", title: "Trade-by-Trade Helpsheet", desc: "Downloadable printable guide covering 23 construction roles — exactly what to write in every CV section. Personal statement examples, skills to select, duties to list, and education for each role." },
            { icon: "⬇️", title: "Instant Downloads", desc: "Download your finished CV as HTML, text, or print directly to PDF. Professional formatting across all 3 templates — ready to email or hand over on site." },
          ].map((f, i) => (
            <div key={i} className="card-hover" style={{ background: "white", borderRadius: 14, padding: "28px 24px", border: "1px solid #ede6df", animation: `fadeUp 0.6s ease ${i * 0.08}s both` }}>
              <div style={{ fontSize: 36, marginBottom: 14, animation: `float ${3 + i * 0.5}s ease-in-out infinite` }}>{f.icon}</div>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 18, color: "#1a1a1a", marginBottom: 8 }}>{f.title}</h3>
              <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What's Inside / Guides Preview */}
      <section id="guides" style={{ background: "#1a1a1a", padding: "80px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(-45deg, transparent, transparent 40px, rgba(230,81,0,0.04) 40px, rgba(230,81,0,0.04) 42px)", pointerEvents: "none" }} />
        <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span style={{ color: "#FFB74D", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>Complete Construction Guide</span>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 5vw, 44px)", color: "white", lineHeight: 1.1, marginTop: 8 }}>Everything You Need to<br />Get on Site</h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
            {[
              { icon: "🪪", title: "CSCS Cards", items: ["6 card types explained", "Requirements for each", "How to apply step-by-step", "CITB test booking info"] },
              { icon: "🎓", title: "New to Industry Tips", items: ["School leaver CV guide", "12 apprenticeship roles & wages", "Transferable skills examples", "How to get started"] },
              { icon: "✅", title: "Job Checklist", items: ["25-step interactive checklist", "CV building steps", "Getting site-ready steps", "Finding work & landing the job"] },
              { icon: "🏗️", title: "Site Preparation", items: ["First day checklist", "Site induction guide", "What to bring daily", "Important site rules"] },
              { icon: "🦺", title: "PPE Guide", items: ["8 essential PPE items", "Required standards (EN)", "Where to buy (UK)", "Cost breakdown"] },
              { icon: "👷", title: "Site Roles & Pay", items: ["Entry to management roles", "Current 2026 UK pay rates", "Daily duty breakdowns", "Career progression paths"] },
              { icon: "💼", title: "Interview Guide", items: ["7 common questions", "Model answers for each", "What to wear on site", "Follow-up tips"] },
              { icon: "🧪", title: "Drug Testing", items: ["Types of tests used", "Detection windows", "What happens if you fail", "Prescription medication advice"] },
            ].map((g, i) => (
              <div key={i} className="card-hover" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "28px 22px", backdropFilter: "blur(4px)" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{g.icon}</div>
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: 17, color: "white", marginBottom: 12 }}>{g.title}</h3>
                {g.items.map((item, j) => (
                  <div key={j} style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", paddingLeft: 16, marginBottom: 6, position: "relative" }}>
                    <span style={{ position: "absolute", left: 0, color: "#E65100" }}>→</span> {item}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span style={{ color: "#E65100", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>One Simple Price</span>
          <h2 className="section-heading" style={{ marginTop: 8 }}>Everything You Need — One Payment</h2>
          <p style={{ color: "#777", fontSize: 17, maxWidth: 520, margin: "12px auto 0" }}>No subscriptions. No upsells. No hidden extras. Pay once, get everything, keep it forever.</p>
        </div>

        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ borderRadius: 20, overflow: "hidden", border: "3px solid #E65100", background: "linear-gradient(180deg, #FFF8F3 0%, white 20%)", boxShadow: "0 24px 80px rgba(230,81,0,0.18)", position: "relative" }}>
            <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", background: "#E65100", color: "white", padding: "6px 28px", borderRadius: "0 0 12px 12px", fontSize: 12, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase" }}>
              COMPLETE BUNDLE
            </div>

            <div style={{ padding: "48px 36px 36px", textAlign: "center" }}>
              <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 26, color: "#1a1a1a", marginBottom: 8 }}>{SINGLE_BUNDLE.name}</h3>
              
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: 64, color: "#E65100", lineHeight: 1 }}>{SINGLE_BUNDLE.price}</span>
              </div>
              <div style={{ color: "#999", fontSize: 15, fontWeight: 500, marginBottom: 24 }}>{SINGLE_BUNDLE.period}</div>

              <button className="cta-btn" onClick={() => window.open(getStripeUrl(), '_blank')} style={{ width: "100%", maxWidth: 420, justifyContent: "center", fontSize: 18, padding: "18px 40px", margin: "0 auto", display: "flex" }}>
                Get Instant Access <span style={{ fontSize: 22 }}>→</span>
              </button>
            </div>

            <div style={{ height: 2, background: "#FFE0B2", margin: "0 36px" }} />

            <div style={{ padding: "28px 36px 36px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#E65100", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Everything included:</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "4px 32px" }}>
                {SINGLE_BUNDLE.features.map((f, j) => (
                  <div key={j} style={{ fontSize: 15, color: "#333", padding: "8px 0", display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ color: "#E65100", fontSize: 16, fontWeight: 800, flexShrink: 0 }}>✓</span> {f}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "#FAFAF7", padding: "20px 36px", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 24px", borderTop: "1px solid #ede6df" }}>
              {SINGLE_BUNDLE.guarantees.map((g, j) => (
                <span key={j} style={{ fontSize: 13, color: "#777", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: "#2E7D32" }}>🛡</span> {g}
                </span>
              ))}
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 28 }}>
            <div style={{ background: "#E8F5E9", display: "inline-flex", alignItems: "center", gap: 10, padding: "12px 24px", borderRadius: 10, fontSize: 14, color: "#2E7D32", fontWeight: 600 }}>
              💡 Less than the price of a meal deal — but it could land you your next job
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section id="faq" style={{ maxWidth: 760, margin: "0 auto", padding: "80px 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <span style={{ color: "#E65100", fontSize: 13, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>Got Questions?</span>
          <h2 className="section-heading" style={{ marginTop: 8 }}>Frequently Asked Questions</h2>
        </div>
        <div style={{ borderTop: "1px solid #e8e0d8" }}>
          {FAQ.map((item, i) => (
            <div key={i} className="faq-item" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div style={{ padding: "18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a" }}>{item.q}</span>
                <span style={{ fontSize: 20, color: "#E65100", fontWeight: 700, flexShrink: 0, transition: "transform 0.3s", transform: openFaq === i ? "rotate(45deg)" : "none" }}>+</span>
              </div>
              {openFaq === i && (
                <div style={{ padding: "0 0 18px", color: "#666", fontSize: 15, lineHeight: 1.65, animation: "fadeUp 0.3s ease" }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #4a2810 100%)", padding: "80px 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(230,81,0,0.06) 60px, rgba(230,81,0,0.06) 62px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 600, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 5vw, 44px)", color: "white", lineHeight: 1.1, marginBottom: 16 }}>Ready to Get<br /><span style={{ color: "#FFB74D" }}>on Site?</span></h2>
          <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 17, marginBottom: 32 }}>Build your construction CV in minutes. Just £9.99 — one payment, lifetime access.</p>
          <button className="cta-btn" onClick={() => window.open(getStripeUrl(), '_blank')} style={{ fontSize: 19, padding: "20px 48px" }}>
            Get Instant Access — £9.99 <span style={{ fontSize: 22 }}>→</span>
          </button>
          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 13, marginTop: 16 }}>One-off payment · 3 CV downloads included · Instant access</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#0f0f0f", padding: "40px 24px 24px", color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20 }}>🏗️</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: "rgba(255,255,255,0.7)" }}>Trade CV Builder</span>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <span style={{ cursor: "pointer" }}>Privacy Policy</span>
            <span style={{ cursor: "pointer" }}>Terms of Service</span>
            <span style={{ cursor: "pointer" }}>info@tradecvbuilder.uk</span>
          </div>
          <div>© 2026 Trade CV Builder. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
